import { useState } from 'react';
import { checkoutSelectionFromUrl } from '../../api/subscriptions';
import AuthLayout from '../../components/AuthLayout';
import AppButton from '../../components/common/AppButton/AppButton';
import Stepper from '../../components/common/Stepper';
import SubscriptionBillingActions from '../../components/subscription/SubscriptionBillingActions';
import type { SubscriptionPeriod } from '../../components/subscription/SubscriptionCard';
import SubscriptionSelector from '../../components/subscription/SubscriptionSelector';
import { billingNote, plans } from '../../config/subscriptionPlans';
import { useAuth } from '../../context/AuthContext';
import { useSignOut } from '../../hooks/useSignOut';
import { useSubscriptionCheckout } from '../../hooks/useSubscriptionCheckout';
import type { PlanId } from '../../types/subscription';

const SubscriptionPage = () => {
  const { hasPermission } = useAuth();
  const { signOut, signingOut } = useSignOut();
  const canManageSubscription = hasPermission('canManageSubscription');

  const [selectedSubsPeriod, setSelectedSubsPeriod] =
    useState<SubscriptionPeriod>(checkoutSelectionFromUrl().period);
  const [selectedPlanId, setSelectedPlanId] = useState<PlanId>(
    checkoutSelectionFromUrl().plan
  );

  const checkout = useSubscriptionCheckout();

  const handleSubscribe = (plan: PlanId) => {
    setSelectedPlanId(plan);
    void checkout.subscribe(plan, selectedSubsPeriod);
  };

  const subtitle = (
    <h3 className="text-base font-normal text-dark text-left">
      Start with a 45-day free trial.{' '}
      {selectedSubsPeriod === 'monthly'
        ? '50% off for 6 paid months as a founding customer.'
        : '50% off your first annual invoice as a founding customer.'}
    </h3>
  );

  return (
    <AuthLayout
      title="Choose your plan"
      subtitle={subtitle}
      stepper={
        <Stepper steps={['Set up your company', 'Subscribe']} currentStep={2} />
      }
      multiColumn={true}
    >
      <SubscriptionSelector
        plans={plans}
        period={selectedSubsPeriod}
        selectedPlanId={selectedPlanId}
        billingNote={billingNote[selectedSubsPeriod]}
        onSelectPlan={setSelectedPlanId}
        onSubscribe={handleSubscribe}
        onPeriodChange={setSelectedSubsPeriod}
        disabled={!canManageSubscription || checkout.busy}
        activeButtonLabel={checkout.buttonLabel}
        loading={checkout.busy}
      />

      {checkout.error && <p role="alert">{checkout.error}</p>}
      {checkout.cancellationPending && checkout.state === 'error' && (
        <AppButton onClick={checkout.retryCancellation}>
          Retry checkout cancellation
        </AppButton>
      )}
      <SubscriptionBillingActions disabled={checkout.busy} />

      {!canManageSubscription && (
        <p className="mt-6 text-center text-sm text-textLight">
          Only the account owner can manage this subscription. Contact them to
          subscribe.
        </p>
      )}

      <div className="mt-6">
        <AppButton
          type="button"
          fullWidth
          variant="tertiary"
          loading={signingOut}
          disabled={signingOut || checkout.state === 'checkout'}
          onClick={signOut}
        >
          Log out
        </AppButton>
      </div>
    </AuthLayout>
  );
};

export default SubscriptionPage;
